import type { OrgAvailableTypeSchema, Schema } from 'types/schema';
import type { SchemaItemResult } from './getEligibleSchemaObjects';

/**
 * Get unique abstract ancestor categories from a list of schema items at a specified depth.
 * Returns abstract ancestors at the given depth level from the schema hierarchy root.
 *
 * @param items    Array of SchemaItemResult objects to derive categories from.
 * @param schemas  Full array of Schema objects to build the parent hierarchy map.
 * @param depth    Depth level to find abstract ancestors (1 = direct children of root, 2 = grandchildren, etc.).
 * @param locale   Locale to use for sorting category names (e.g., 'en-US').
 * @returns        Array of Schema objects representing unique abstract ancestor categories at the specified depth,
 *                 sorted by localized name.
 */
export function getSchemaCategories<
  T extends OrgAvailableTypeSchema = Schema
>(
  items: SchemaItemResult<T>[],
  schemas: T[],
  depth = 1,
  locale = 'en-US'
): T[] {
  // Helper: pick localized name or fall back to s.name
  const localizedName = (s: T): string => {
    return s.schema_name_translations?.[locale] ?? s.name;
  };

  // Build schema_id → Schema map
  const schemaMap = new Map<string, T>();
  schemas.forEach((s) => {
    schemaMap.set(s.schema_id, s);
  });

  // Helper: find abstract ancestor at specified depth
  const findAbstractAncestorAtDepth = (
    schema: T,
    targetDepth: number,
    schemaMap: Map<string, T>
  ): T | null => {
    // First, build the path from current schema to root
    const pathToRoot: T[] = [];
    let current = schema;
    const visited = new Set<string>();

    // Traverse up to root, building the path
    while (current) {
      if (visited.has(current.schema_id)) {
        break; // Prevent infinite loops
      }
      visited.add(current.schema_id);
      pathToRoot.push(current);

      if (!current.parent_schema_id) {
        break; // Reached root
      }

      const parent = schemaMap.get(current.parent_schema_id);
      if (!parent) {
        break; // Parent not found
      }
      current = parent;
    }

    // Reverse to get root-to-leaf order
    pathToRoot.reverse();

    // Find the schema at the target depth that is abstract
    // depth=1 means first level after root, depth=2 means second level, etc.
    if (pathToRoot.length <= targetDepth) {
      return null; // Not deep enough
    }

    const candidateAtDepth = pathToRoot[targetDepth];
    
    // Check if this candidate is abstract
    if (candidateAtDepth.is_abstract) {
      return candidateAtDepth;
    }

    // If not abstract at exact depth, look for the nearest abstract ancestor
    // within the depth constraint (going backwards from target depth towards root)
    for (let i = targetDepth - 1; i >= 0; i--) {
      if (pathToRoot[i].is_abstract) {
        return pathToRoot[i];
      }
    }

    return null;
  };

  // Derive unique abstract ancestor categories at specified depth
  const categoryMap = new Map<string, T>();
  items.forEach((item) => {
    const ancestor = findAbstractAncestorAtDepth(item.schema, depth, schemaMap);
    if (ancestor) {
      categoryMap.set(ancestor.schema_id, ancestor);
    }
  });

  // Convert to array and sort by localized name
  const categories = Array.from(categoryMap.values());
  categories.sort((a, b) => {
    const aName = localizedName(a);
    const bName = localizedName(b);
    return aName.localeCompare(bName, locale);
  });

  return categories;
}